/*
 * Copyright (c) 2019
 *    Reliant Business Valuation, LLC - http://reliantvalue.com
 *    Author: Kirill Vizniuk - kirill@internalvaluation.com
 *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 * http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 */

import {SearchableItem} from './interface/searchable.item';
import {SearchableSelectionType} from './interface/searchable.selection';

export class Client implements SearchableItem {
  ID: string;
  COMPANY_NAME: string;
  ADDRESS: string;
  ADDRESS2: string;
  CITY: string;
  STATE: string;
  ZIP: string;
  PHONE: string;
  PHONE_EXT: string;
  FAX: string;
  WEB: string;
  IS_BANK: boolean;
  NOTES: string;
  DOMAINS: string;
  COLLECTOR: boolean;
  TEMPLATE: string;
  IS_LSP: boolean;
  NEW_BANK:  boolean;

  // Helpers
  active = false;
  selected = false;
  allowed = false;

  static parse(json: any): Client {
    if (json) {
      const client = new Client();

      client.ID = json.ID;
      client.COMPANY_NAME = json.COMPANY_NAME;
      client.ADDRESS = json.ADDRESS;
      client.ADDRESS2 = json.ADDRESS2;
      client.CITY = json.CITY;
      client.STATE = json.STATE;
      client.ZIP = json.ZIP;
      client.PHONE = json.PHONE;
      client.PHONE_EXT = json.PHONE_EXT;
      client.FAX = json.FAX;
      client.WEB = json.WEB;
      client.NOTES = json.NOTES;
      client.IS_BANK = json.IS_BANK;
      client.DOMAINS = json.DOMAINS;
      client.COLLECTOR = json.COLLECTOR;
      client.TEMPLATE = json.TEMPLATE;
      client.IS_LSP = json.IS_LSP;
      client.NEW_BANK = json.NEW_BANK;

      return client;
    } else return null;
  }

  static parseArray(json: any[]): Client[] {
    const arr = [];

    if (json)
      for (const j of json)
        if (j)
          arr.push(Client.parse(j));

    return arr;
  }

  getName(): string {
    return this.COMPANY_NAME;
  }

  getFullDetails(): string {
    return this.COMPANY_NAME;
  }

  getType(): SearchableSelectionType {
    return SearchableSelectionType.VALUE;
  }

  getValue(): any {
    return this;
  }

  getFullAddress(): string {
    let buff = '';

    if (this.ADDRESS) {
      if (this.ADDRESS.length > 0)
        buff += this.ADDRESS;
    }

    if (this.ADDRESS2) {
      if (this.ADDRESS2.length > 0) {
        if (buff.length > 0)
          buff += ', ';

        buff += this.ADDRESS2;
      }
    }

    if (this.CITY) {
      if (this.CITY.length > 0) {
        if (buff.length > 0)
          buff += ', ';

        buff += this.CITY;
      }
    }

    if (this.STATE) {
      if (this.STATE.length > 0) {
        if (buff.length > 0)
          buff += ', ';

        buff += this.STATE;
      }
    }

    if (this.ZIP) {
      if (this.ZIP.length > 0) {
        if (buff.length > 0)
          buff += ' ';

        buff += this.ZIP;
      }
    }

    return buff;
  }

  getFullPhone(): string {
    let buff = '';

    if (this.PHONE)
      if (this.PHONE.length > 0)
        buff += this.PHONE;

    if (this.PHONE_EXT)
      if (this.PHONE_EXT.length > 0) {
        if (buff.length > 0)
          buff += ', ';

        buff += this.PHONE_EXT;
      }

    return buff;
  }
}

export class ClientUser implements SearchableItem {
  ID: string;
  NAME: string;
  ADDRESS: string;
  ADDRESS2: string;
  CITY: string;
  STATE: string;
  ZIP: string;
  PHONE_OFFICE: string;
  PHONE_EXT: string;
  PHONE_MOBILE: string;
  PHONE_FAX: string;
  EMAIL: string;
  NOTES: string;
  PHONE_PERSONAL: string;
  TITLE: string;
  LINKEDIN_URL: string;

  ENG_ADDRESS: string;
  ENG_ADDRESS_USE_FILE: boolean;

  // Back Reference
  CLIENT: Client;

  // Helpers
  active = false;
  selected = false;
  allowed = false;

  static parse(json: any): ClientUser {
    if (json) {
      const c = new ClientUser();

      c.ID = json.ID;
      c.EMAIL = json.EMAIL;
      c.NAME = json.NAME;
      c.ADDRESS = json.ADDRESS;
      c.ADDRESS2 = json.ADDRESS2;
      c.CITY = json.CITY;
      c.STATE = json.STATE;
      c.ZIP = json.ZIP;
      c.PHONE_OFFICE = json.PHONE_OFFICE;
      c.PHONE_EXT = json.PHONE_EXT;
      c.PHONE_MOBILE = json.PHONE_MOBILE;
      c.PHONE_FAX = json.PHONE_FAX;
      c.NOTES = json.NOTES;
      c.PHONE_PERSONAL = json.PHONE_PERSONAL;
      c.TITLE = json.TITLE;
      c.LINKEDIN_URL = json.LINKEDIN_URL;

      c.CLIENT = Client.parse(json.CLIENT);

      c.ENG_ADDRESS = json.ENG_ADDRESS;
      c.ENG_ADDRESS_USE_FILE = json.ENG_ADDRESS_USE_FILE;

      return c;
    } else return null;
  }

  static parseArray(json: any[]): ClientUser[] {
    const arr = [];

    if (json)
      for (const j of json)
        if (j)
          arr.push(ClientUser.parse(j));

    return arr;
  }

  getName(): string {
    return `${this.NAME ? this.NAME : this.EMAIL}`;
  }

  getFullDetails(): string {
    return `${this.NAME ? this.NAME: ''} ${this.EMAIL ? '<' + this.EMAIL + '>' : '' }`;
  }

  getType(): SearchableSelectionType {
    return SearchableSelectionType.VALUE;
  }

  getValue(): any {
    return this;
  }

  getFullPhone(): string {
    let buff = '';

    if (this.PHONE_OFFICE)
      if (this.PHONE_OFFICE.length > 0)
        buff += this.PHONE_OFFICE;

    if (this.PHONE_EXT)
      if (this.PHONE_EXT.length > 0) {
        if (buff.length > 0)
          buff += ', ';

        buff += this.PHONE_EXT;
      }

    return buff;
  }

  getFullAddress(): string {
    let buff = '';

    if (this.ADDRESS) {
      if (this.ADDRESS.length > 0)
        buff += this.ADDRESS;
    }

    if (this.ADDRESS2) {
      if (this.ADDRESS2.length > 0) {
        if (buff.length > 0)
          buff += ', ';

        buff += this.ADDRESS2;
      }
    }

    if (this.CITY) {
      if (this.CITY.length > 0) {
        if (buff.length > 0)
          buff += ', ';

        buff += this.CITY;
      }
    }

    if (this.STATE) {
      if (this.STATE.length > 0) {
        if (buff.length > 0)
          buff += ', ';

        buff += this.STATE;
      }
    }

    if (this.ZIP) {
      if (this.ZIP.length > 0) {
        if (buff.length > 0)
          buff += ' ';

        buff += this.ZIP;
      }
    }

    return buff;
  }
}
